/**
 * Searchable multi-select with checkbox options and a compact selection summary.
 * Used by ticket analysis, agent configure filters, and Observer scope filters.
 */

import React, { useMemo, useState } from 'react';
import Autocomplete, {
  createFilterOptions,
  type AutocompleteChangeDetails,
  type AutocompleteChangeReason,
} from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';
import { OVERLAY_SAFE_POPUP_Z_INDEX } from '../utils/overlayMenu';
import { disambiguateOptionLabels } from '../utils/selectOptionLabels';
import {
  OBSERVER_SELECT_ALL_VALUE,
  type ObserverSelectOption,
  buildObserverMultiSelectLabel,
  isAllObserverOptionsSelected,
  resolveObserverMultiSelectChange,
  withSelectAllOption,
} from '../observer/utils/observerMultiSelect';

export interface LayeredMultiSelectOption {
  label: string;
  value: string;
}

const filterOptions = createFilterOptions<LayeredMultiSelectOption>({
  stringify: (option) => `${option.label} ${option.value}`,
});

interface LayeredMultiAutocompleteSelectProps {
  label: string;
  options: LayeredMultiSelectOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  size?: TextFieldProps['size'];
  helperText?: React.ReactNode;
  noOptionsText?: string;
  listboxMaxHeight?: number;
  /** When true, prepends a Select All row (default true). */
  includeSelectAll?: boolean;
  selectAllLabel?: string;
  allSelectedLabel?: string;
  noneSelectedLabel?: string;
  /** For filters where an empty selection means no restriction. The All row clears the filter. */
  emptySelectionMeansAll?: boolean;
  disableClearable?: boolean;
  inputAriaLabel?: string;
  singularCountLabel?: string;
  pluralCountLabel?: string;
  sx?: SxProps<Theme>;
  disablePortal?: boolean;
}

const LayeredMultiAutocompleteSelect: React.FC<LayeredMultiAutocompleteSelectProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  disabled = false,
  loading = false,
  fullWidth = true,
  size,
  helperText,
  noOptionsText = 'No options',
  listboxMaxHeight = 400,
  includeSelectAll = true,
  selectAllLabel = 'Select All',
  allSelectedLabel = 'All',
  noneSelectedLabel = 'None selected',
  emptySelectionMeansAll = false,
  disableClearable = true,
  inputAriaLabel,
  singularCountLabel = 'item',
  pluralCountLabel = 'items',
  sx,
  disablePortal = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const displayOptions = useMemo(() => disambiguateOptionLabels(options), [options]);
  const optionValues = useMemo(
    () => options.map((option) => option.value),
    [options],
  );

  const listOptions = useMemo<ObserverSelectOption[]>(
    () => (includeSelectAll ? withSelectAllOption(displayOptions, selectAllLabel) : displayOptions),
    [includeSelectAll, displayOptions, selectAllLabel],
  );

  const selectedOptions = useMemo(
    () => displayOptions.filter((option) => selectedValues.includes(option.value)),
    [displayOptions, selectedValues],
  );

  const allSelected = emptySelectionMeansAll
    ? selectedValues.length === 0
    : isAllObserverOptionsSelected(optionValues, selectedValues);

  const displayLabel = selectedValues.length === 1
    ? selectedOptions[0]?.label || selectedValues[0]
    : buildObserverMultiSelectLabel(
        selectedValues,
        optionValues,
        singularCountLabel,
        pluralCountLabel,
        emptySelectionMeansAll ? `${selectedValues.length} ${pluralCountLabel}` : allSelectedLabel,
        emptySelectionMeansAll ? allSelectedLabel : noneSelectedLabel,
      );

  const handleChange = (
    _event: React.SyntheticEvent,
    nextOptions: LayeredMultiSelectOption[],
    _reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<LayeredMultiSelectOption>,
  ) => {
    if (emptySelectionMeansAll && details?.option?.value === OBSERVER_SELECT_ALL_VALUE) {
      onChange([]);
      return;
    }
    const nextValues = resolveObserverMultiSelectChange(
      nextOptions.map((option) => option.value),
      optionValues,
      details?.option?.value,
    );
    onChange(nextValues);
  };

  return (
    <Autocomplete<LayeredMultiSelectOption, true, boolean, false>
      multiple
      disableCloseOnSelect
      openOnFocus
      disablePortal={disablePortal}
      options={listOptions}
      value={selectedOptions}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      getOptionKey={(option) => option.value}
      isOptionEqualToValue={(option, selected) => option.value === selected.value}
      filterOptions={filterOptions}
      disabled={disabled || loading}
      loading={loading}
      noOptionsText={noOptionsText}
      fullWidth={fullWidth}
      disableClearable={disableClearable}
      inputValue={inputValue}
      onInputChange={(_, value) => setInputValue(value)}
      renderTags={() => inputValue ? null : (
        <Typography
          sx={{
            fontSize: 13,
            maxWidth: '100%',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {displayLabel}
        </Typography>
      )}
      renderOption={(props, option) => {
        const isSelectAll = option.value === OBSERVER_SELECT_ALL_VALUE;
        const checked = isSelectAll
          ? allSelected
          : selectedValues.includes(option.value);
        return (
          <li {...props} key={option.value} role="option" aria-selected={checked}>
            <Checkbox
              checked={checked}
              indeterminate={
                isSelectAll && !emptySelectionMeansAll && selectedValues.length > 0 && !allSelected
              }
              size="small"
              sx={{ mr: 1, py: 0 }}
            />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: isSelectAll ? 600 : 400,
              }}
            >
              {option.label}
            </Typography>
          </li>
        );
      }}
      slotProps={{
        popper: {
          sx: {
            zIndex: `${OVERLAY_SAFE_POPUP_Z_INDEX} !important`,
            pointerEvents: 'auto',
          },
        },
        paper: {
          sx: {
            maxHeight: listboxMaxHeight,
            overflow: 'hidden',
          },
        },
      }}
      ListboxProps={{
        style: {
          maxHeight: listboxMaxHeight,
          overflowY: 'auto',
        },
      }}
      sx={{
        '& .MuiAutocomplete-paper': {
          maxHeight: listboxMaxHeight,
        },
        '& .MuiAutocomplete-listbox': {
          maxHeight: listboxMaxHeight,
          overflowY: 'auto',
        },
        ...((sx || {}) as object),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size={size}
          label={label}
          InputLabelProps={{ shrink: true }}
          placeholder={selectedValues.length ? undefined : emptySelectionMeansAll ? allSelectedLabel : noneSelectedLabel}
          helperText={helperText}
          sx={{
            '& .MuiInputLabel-root': { fontFamily: 'var(--theo-font-sans)' },
            '& .MuiInputBase-root': { fontFamily: 'var(--theo-font-sans)' },
          }}
          inputProps={{
            ...params.inputProps,
            ...(inputAriaLabel ? { 'aria-label': inputAriaLabel } : {}),
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default LayeredMultiAutocompleteSelect;
